import { getDeviceSelectionCategories } from './deviceSelectionCategoryUtilsCoCo.js';
import { toDeviceSelectionProductType } from './deviceSelectionListUtilsCoCo.js';
import { eurosToCents } from './priceUtils.js';
import type { DeviceSelectionDeviceCoCo } from './deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceSelectionFormValuesCoCo } from './deviceSelectionFormUtilsCoCo.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';
import type { PatchEmployeeProductSelectionRequest } from '../../generated/api/patchEmployeeProductSelectionRequest.js';

// The devices must be the ones of the product type of the selection
export const getDeviceSelectionEditDevices = (
  devices: DeviceSelectionDeviceCoCo[],
  selection: EmployeeProductSelectionResponse
): DeviceSelectionDeviceCoCo[] => {
  const availableCategories = new Set(
    getDeviceSelectionCategories(toDeviceSelectionProductType(selection.productType)).map(({ category }) => category)
  );
  const selectedItems = new Map(selection.items.map(item => [item.variantGroupId, item]));
  return devices
    .filter(device => availableCategories.has(device.category))
    .map(device => {
      const selectedItem = selectedItems.get(device.id);
      if (!selectedItem) {
        return device;
      }
      const editedDevice = { ...device, name: selectedItem.variantGroupName };
      if (selectedItem.price !== undefined) {
        const priceInCents = eurosToCents(selectedItem.price);
        if (device.oneTimePrice === undefined) {
          editedDevice.monthlyPrice = priceInCents;
        } else {
          editedDevice.oneTimePrice = priceInCents;
        }
      }
      return editedDevice;
    });
};

export const getDeviceSelectionPatch = (
  original: EmployeeProductSelectionResponse,
  values: DeviceSelectionFormValuesCoCo,
  devices: DeviceSelectionDeviceCoCo[]
): PatchEmployeeProductSelectionRequest => {
  const selectedIds = new Set(values.selectedDeviceIds);
  const originalIds = new Set(original.items.map(item => item.variantGroupId));
  return {
    name: values.name,
    description: values.description,
    items: [
      ...original.items
        .filter(item => !selectedIds.has(item.variantGroupId))
        .map(item => ({ action: 'remove' as const, uuid: item.uuid })),
      ...values.selectedDeviceIds
        .filter(id => !originalIds.has(id))
        .map(id => {
          const device = devices.find(item => item.id === id);
          if (!device) {
            throw new Error(`Device selection device ${id} is not available`);
          }
          return { action: 'add' as const, variantGroupId: id, variantGroupName: device.name };
        }),
    ],
  };
};
